import { useState, useEffect, useCallback } from 'react';
import { UserAuth, UserRole } from '../components/SidebarConfig';
import api from '../utils/api';
import { getClientIP, getSystemInfo } from '../utils/clientInfo';
import { generateDeviceFingerprint } from '../utils/deviceFingerprint';
import { setLegacyToken } from '../../config/etaxToken';

export const useAuth = () => {
  const [userAuth, setUserAuth] = useState<UserAuth>({
    role: 'accountant',
    permissions: ['view_customers', 'view_reports'],
    userId: '',
    username: '',
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  // Function to update userAuth from localStorage
  const updateUserAuthFromStorage = useCallback(() => {
    const storedToken = localStorage.getItem('novaqToken');
    const username = localStorage.getItem('username');
    const accountantOid = localStorage.getItem('accountantOid');
    const userRole = localStorage.getItem('userRole') as UserRole || 'accountant';

    // Debug: Check localStorage values
    console.log('🔍 updateUserAuthFromStorage - localStorage values:');
    console.log('  - username:', username);
    console.log('  - accountantOid:', accountantOid);
    console.log('  - userRole:', userRole);

    if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);
    }

    // Define permissions based on role
    const getPermissionsForRole = (role: UserRole): string[] => {
      switch (role) {
        case 'admin':
          return [
            'view_customers',
            'manage_customers',
            'view_reports',
            'generate_reports',
            'manage_accountants',
            'view_accountants',
            'system_settings',
            'manage_users',
            'view_performance',
            'view_ebarimt',
            'view_accounts',
            'view_licenses',
            'view_journal',
            'view_general_journal',
            'view_salary',
            'view_netizen',
            'view_business_relations',
            'view_balance',
            'view_guide',
            'view_receipts',
            'view_summary_month',
            'view_summary_year',
            'view_warnings',
            'view_customs',
            'view_blacklist',
            'view_resolution_report',
            'view_consolidated_report',
            'view_peace_of_mind',
            'view_tax_details',
            'view_tax_payment',
            'view_analytics',
            'view_display',
            'view_report',
            'view_1112_report',
            'view_1111_report',
            'view_balance_sheet',
            'view_income_statement',
            'view_cash_flow',
          ];
        case 'accountant':
          return [
            'view_customers',
            'manage_customers',
            'view_reports',
            'generate_reports',
            'view_ebarimt',
            'manage_users',
            'manage_accountants',
            'view_accountants',
          ];
       
        default:
          return ['view_customers', 'view_reports'];
      }
    };

    const permissions = getPermissionsForRole(userRole);

    setUserAuth({
      role: userRole,
      permissions,
      username: username || '',
      accountantOid: accountantOid || '',
    });

    // Debug: Check setUserAuth result
    console.log('🔍 setUserAuth called with:');
    console.log('  - role:', userRole);
    console.log('  - username:', username || '');
    console.log('  - accountantOid:', accountantOid || '');
  }, [setUserAuth]);

  useEffect(() => {
    // App эхлэхэд localStorage-оос мэдээлэл унших
    updateUserAuthFromStorage();
    
    // Token байгаа эсэхийг шалгах
    const storedToken = localStorage.getItem('novaqToken');
    if (storedToken) {
      setToken(storedToken);
      setIsAuthenticated(true);
      console.log('🔑 App эхлэхэд token олдлоо:', storedToken.length, 'тэмдэгт');
    } else {
      console.log('🔑 App эхлэхэд token олдоогүй');
    }
  }, [updateUserAuthFromStorage]);

  const updateUserRole = (newRole: UserRole) => {
    localStorage.setItem('userRole', newRole);
    
    // Define permissions based on role
    const getPermissionsForRole = (role: UserRole): string[] => {
      switch (role) {
        case 'admin':
          return [
            'view_customers',
            'manage_customers',
            'view_reports',
            'generate_reports',
            'manage_accountants',
            'view_accountants',
            'system_settings',
            'manage_users',
            'view_performance',
            'view_ebarimt',
            'view_accounts',
            'view_licenses',
            'view_journal',
            'view_general_journal',
            'view_salary',
            'view_netizen',
            'view_business_relations',
            'view_balance',
            'view_guide',
            'view_receipts',
            'view_summary_month',
            'view_summary_year',
            'view_warnings',
            'view_customs',
            'view_blacklist',
            'view_resolution_report',
            'view_consolidated_report',
            'view_peace_of_mind',
            'view_tax_details',
            'view_tax_payment',
            'view_analytics',
            'view_display',
            'view_report',
            'view_1112_report',
            'view_1111_report',
            'view_balance_sheet',
            'view_income_statement',
            'view_cash_flow',
          ];
        case 'accountant':
          return [
            'view_customers',
            'manage_customers',
            'view_reports',
            'generate_reports',
            'view_ebarimt',
            'manage_users',
            'manage_accountants',
            'view_accountants',
          ];
        default:
          return ['view_customers', 'view_reports'];
      }
    };
    
    setUserAuth(prev => ({
      ...prev,
      role: newRole,
      permissions: getPermissionsForRole(newRole),
    }));
  };


  const hasPermission = (permission: string): boolean => {
    return userAuth.permissions.includes(permission);
  };

  const login = async (username: string, password: string, result?: any) => {
    // Token цэвэрлэх - etaxToken.ts-аас clearEtaxTokens() ашиглах
    try {
      const deviceId = generateDeviceFingerprint();
      let data;
      
      if (result) {
        // Use the result object passed from Home.tsx
        data = result;
        console.log('🔍 Using result object from Home.tsx:', data);
      } else {
        // Make API call if no result object provided
        const response = await fetch('/api/auth', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-device-id': deviceId, // RE-ENABLED
          },
          body: JSON.stringify({
            UserName: username,
            PasswordHash: password,
          }),
        });

        data = await response.json();
      }

      if (data.success) {
        console.log('🔍 Login response data:', data.token);
        const { token:novaqToken, data: userData } = data; // token re-enabled
        
        // Debug: Check login response data
        console.log('🔍 Login response userData:', userData);
        console.log('🔍 userData.Oid:', userData.Oid);
        console.log('🔍 userData.AccountantOid:', userData.AccountantOid);
        console.log('🔍 Token from data:', novaqToken);
        console.log('🔍 Token type:', typeof novaqToken);
        console.log('🔍 Token length:', novaqToken ? novaqToken.length : 0);
        
        // Store token and user data - TOKEN RE-ENABLED
        console.log('🔍 Token stored in localStorage as novaqToken',novaqToken);
        if (novaqToken) {
          setLegacyToken(token); // RE-ENABLED
          console.log('✅ Token stored in localStorage as novaqToken');
        } else {
          console.error('❌ No token in response data');
        }

        localStorage.setItem('username', userData.UserName);   
        localStorage.setItem('userRole', userData.RoleName?.toLowerCase() || 'accountant');
        localStorage.setItem('accountantOid', userData.Oid);
        localStorage.setItem('deviceId', deviceId);
        localStorage.setItem('novaqToken', novaqToken);

        // Debug: Check localStorage after setting
        console.log('🔍 After localStorage.setItem:');
        console.log('  - localStorage.getItem("novaqToken"):', localStorage.getItem('novaqToken') ? 'YES' : 'NO');
        console.log('  - localStorage.getItem("accountantOid"):', localStorage.getItem('accountantOid'));
        console.log('  - localStorage.getItem("username"):', localStorage.getItem('username'));

        setToken(token); // RE-ENABLED
        setIsAuthenticated(true);
        
        // Update user auth state from localStorage
        updateUserAuthFromStorage();
        
        // Debug: Final check
        console.log('🔑 Login амжилттай - Final state:');
        console.log('  - isAuthenticated:', true);
        console.log('  - token length:', token ? token.length : 0);
        console.log('  - localStorage novaqToken:', localStorage.getItem('novaqToken') ? 'YES' : 'NO');

        // Note: Login history is now saved automatically by backend during authentication
        console.log('✅ Login history will be saved by backend during authentication process');

        return { success: true, data: userData };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Login failed' };
    }
  };

  const logout = () => {
    // Clear all stored data
    localStorage.removeItem('novaqToken'); // RE-ENABLED
    localStorage.removeItem('username');
    localStorage.removeItem('userOid');
    localStorage.removeItem('userRole');
    localStorage.removeItem('accountantOid');
    localStorage.removeItem('deviceId');
    
    // Reset state
    setToken(null); // RE-ENABLED
    setIsAuthenticated(false);
    setUserAuth({
      role: 'accountant',
      permissions: ['view_customers', 'view_reports'],
      userId: '',
      username: '',
      accountantOid: '',
    });
  };

  // Check if token is valid - RE-ENABLED
  const checkAuth = async () => {
    const storedToken = localStorage.getItem('novaqToken'); // RE-ENABLED
    console.log('🔑 checkAuth - Token check:', storedToken ? 'Token exists' : 'No token');
    
    if (!storedToken) {
      console.log('🔑 checkAuth - No token, setting unauthenticated');
      setIsAuthenticated(false);
      setToken(null);
      return false;
    }

    try {
      // Simple token validation - just check if token exists and is not expired
      // In a real application, you might want to validate the token structure
      if (storedToken && storedToken.length > 0) {
        console.log('🔑 checkAuth - Token valid, setting authenticated');
        setIsAuthenticated(true);
        setToken(storedToken);
        return true;
      } else {
        console.log('🔑 checkAuth - Token invalid, logging out');
        logout();
        return false;
      }
    } catch (error) {
      console.error('🔑 checkAuth - Auth check error:', error);
      logout();
      return false;
    }
  };

  // Get auth headers for API calls - RE-ENABLED
  const getAuthHeaders = () => {
    const storedToken = localStorage.getItem('novaqToken'); // RE-ENABLED
    return storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {}; // RE-ENABLED
  };

  return {
    userAuth,
    isAuthenticated,
    token,
    updateUserRole,
    hasPermission,
    login,
    logout,
    checkAuth,
    getAuthHeaders,
  };
}; 